/**
 * Native Mixpanel super props bridged from saas-app via postMessage (`setMpDistinctId`).
 * initializers.js writes them to window.__RN_MIXPANEL_NATIVE_PROPS__ because Segment does
 * not expose registered props through window.mixpanel.get_property.
 */

import { getDistinctId, identifyUser } from 'user-analytics';
import { isNativeAppWebView } from 'utils/webview';

/**
 * Returns the raw native props stash from the React Native WebView bridge.
 * @returns {Object} Native super props or an empty object outside a WebView / before postMessage.
 */
export function getRnMixpanelNativeProps() {
  if (typeof window === 'undefined') {
    return {};
  }
  // eslint-disable-next-line no-underscore-dangle
  return window.__RN_MIXPANEL_NATIVE_PROPS__ || {};
}

/**
 * Distinct id injected by saas-app before the funnel bundle loads (injectedJavaScriptBeforeContentLoaded).
 * @returns {string|null} Pending distinct id or null when not in a WebView / before injection.
 */
export function getRnMixpanelPendingDistinctId() {
  if (typeof window === 'undefined') {
    return null;
  }
  // eslint-disable-next-line no-underscore-dangle
  return window.__RN_PENDING_DISTINCT_ID__ || null;
}

/**
 * Returns native props for client-side Mixpanel events (Purchase Attempt, etc.).
 * Keys match the saas-app bridge: App Version, platform, product, WebView, etc.
 * @returns {Object} Event properties to merge into track payloads, or {} when unavailable.
 */
export function getRnMixpanelNativeEventProps() {
  const props = getRnMixpanelNativeProps();
  return Object.keys(props).length ? { ...props } : {};
}

/**
 * Returns native props for the platform-purchase newPurchase API payload.
 * @returns {Object} snake_case fields for app_version, platform, product, android_api_version.
 */
export function getRnMixpanelNativePurchasePayloadProps() {
  const props = getRnMixpanelNativeProps();
  return {
    app_version: props['App Version'],
    platform: props.platform,
    product: props.product,
    android_api_version: props['Android API Version'],
  };
}

/**
 * Traits for identifyUser when bridging the native Mixpanel distinct id.
 * user-analytics requires a traits object (reads traits['User Type']).
 * @returns {Object} Identify traits including native super props when available.
 */
export function getRnMixpanelIdentifyTraits() {
  return {
    'User Type': 'Anonymous',
    ...getRnMixpanelNativeEventProps(),
  };
}

/**
 * Identifies the web Mixpanel user with the native distinct id before client events fire.
 * Avoids Android races where Search runs before Segment onLoaded / loadEnd postMessage.
 */
export function ensureRnMixpanelIdentified() {
  if (!isNativeAppWebView()) {
    return;
  }

  const distinctId = getRnMixpanelPendingDistinctId();
  if (!distinctId) {
    return;
  }

  // eslint-disable-next-line no-underscore-dangle
  if (window.__RN_MIXPANEL_IDENTIFIED_AS__ === distinctId) {
    return;
  }

  identifyUser(distinctId, getRnMixpanelIdentifyTraits());
  // eslint-disable-next-line no-underscore-dangle
  window.__RN_MIXPANEL_IDENTIFIED_AS__ = distinctId;
}

/**
 * Returns the Mixpanel distinct id for API payloads (e.g. purchase.tracker_id).
 * Prefers the native id bridged into the WebView over mixpanel-js's anonymous id.
 * @returns {string} Native bridged id in WebView, otherwise the current web distinct id.
 */
export function getRnMixpanelBridgedTrackerId() {
  if (isNativeAppWebView()) {
    const bridgedId = getRnMixpanelPendingDistinctId();
    if (bridgedId) {
      return bridgedId;
    }
  }

  return getDistinctId();
}
